import { useCallback, useState } from "react";
import { WpAssetMetaBoxData } from "@hooks/use-get/assets/types";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { sanitizeObjectData } from "@hooks/use-get/utils";
import { useScreenAlert } from "@hooks/use-screen-alert";

//* added "postType" arg for reusability
export const useUpdateAsset = (postType: string = "assets") => {
    const [loading, setLoading] = useState(false);
    const { setError } = useScreenAlert();

    const { user } = useUserContext();
    const { getApiUrl } = useMultiSiteContext();
    const { refreshUserToken } = useRefreshTokenMutation();

    //* Added to accept postType arg
    const WP_ENDPOINT: string = `wp-json/wp/v2/${postType}/`;

    const updateAsset = useCallback(
        async (id: string | number, input: Partial<WpAssetMetaBoxData>) => {
            setLoading(true);

            // Sanitize input data to prevent XSS and injection
            const sanitizedInput = sanitizeObjectData(input);

            try {
                const url = getApiUrl(`${WP_ENDPOINT}${id}`);
                const response = await fetchWithTokenRefresh(
                    url,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${user?.authToken ?? ""}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(sanitizedInput),
                    },
                    () => refreshUserToken(user?.refreshToken ?? "")
                );

                if (!response.ok) {
                    const body = await response.json().catch(() => null);
                    const serverMessage: string | undefined = body?.message;
                    if (response.status === 409) {
                        setError(serverMessage ?? "This value is already in use. Please check the highlighted fields.");
                    } else {
                        setError(serverMessage ?? "Failed to save asset. Please try again.");
                        console.error("[useUpdateAsset] HTTP", response.status, serverMessage ?? "");
                    }
                    setLoading(false);
                    return undefined;
                }

                setLoading(false);
                return await response.json();
            } catch (err) {
                setError("Failed to save asset. Please try again.");
                console.error("[useUpdateAsset]", err);
                setLoading(false);
                return undefined;
            }
        },
        [getApiUrl, user?.authToken, user?.refreshToken, refreshUserToken, setError, WP_ENDPOINT]
    );

    return {
        updateAsset,
        loading,
    };
};
